import React, { PropTypes } from 'react'
import Helmet from 'react-helmet'
import { Link as RouterLink } from 'react-router'
import { prefixLink } from 'gatsby-helpers'

import { colors, breakpoints } from '../constants/theme'

import Section from '../components/Section'
import Column from '../components/Column'
import Row from '../components/Row'
import Spacer from '../components/Spacer'
import Container from '../components/Container'
import Page from '../components/Page'
import Icon from '../components/Icon'
import IconLink from '../components/IconLink'
import Image from '../components/Image'
import SlideButton from '../components/SlideButton'

import Heading1 from '../styleguide/Heading1'
import Heading2 from '../styleguide/Heading2'
import Heading3 from '../styleguide/Heading3'
import Paragraph from '../styleguide/Paragraph'
import Navigation from '../styleguide/Navigation'
import LinkList from '../styleguide/LinkList'
import Footer from '../styleguide/Footer'
import Subtext from '../styleguide/Subtext'
import Services from '../styleguide/Services'
import Quote from '../styleguide/Quote'
import QuoteAuthor from '../styleguide/QuoteAuthor'
import Button from '../styleguide/Button'
import Line from '../styleguide/Line'

import FelaProvider from '../containers/FelaProvider'
import DeviceProvider from '../containers/DeviceProvider'

import posts from '../constants/blogPosts'

import './fontAwesome/css/font-awesome.css'

const { min2560, min1920, min1440, min1024, min768, min480, min360 } = breakpoints

function getMacbookRight(width) {
  if (width >= min1920) {
    return (width - 1404) / 2
  }
  if (width >= min1440) {
    return (width - 1170) / 2 - 50
  }
  if (width >= min1024 + 180) {
    return -100
  }
  if (width >= min1024) {
    return -200
  }

  if (width >= min1024 - (min1024 - min768) / 2) {
    return -250
  }
  if (width >= min768) {
    return -320
  }

  return 0
}

function getMacbookTop(width) {
  if (width >= min1440) {
    return 20
  }

  if (width >= min768) {
    return -50
  }
}

const metaData = [
  {
    name: 'description',
    content:
      'Wir entwickeln Ihre Lösung auf aktuellsten Web- und IoT Stacks.'
  },
  {
    name: 'keywords',
    content:
      'functional, elixir, erlang, elm, react, react native, graphql, node, node.js, javascript, ruby, agency, agentur, entwicklung, entwickler, development, karlsruhe'
  }
]

function scrollTo(id, fixedOffsetTop) {
  const offsetTop =
    fixedOffsetTop !== undefined
      ? fixedOffsetTop
      : document.querySelectorAll(`[data-scroll-scope=${id}]`)[0].offsetTop + window.innerHeight
  window.IS_SCROLLING = Date.now() + 468
  window.scroll({
    top: offsetTop,
    left: 0,
    behavior: 'smooth'
  })
}

const Navigator = () => (
  <Page
    id="navigation"
    style={{
      position: 'fixed',
      zIndex: 10,
      transformStyle: 'preserve-3d'
    }}
  >
    <Container color="white" flex="0 0 auto">
      <Row alignItems="center" justifyContent="space-between">
        <Column
          alignItems="flex-start"
          justifyContent="space-between"
          sizes={{ [min768]: 3 }}
          onClick={() => scrollTo('landing', 0)}
        >
          <img
            src={prefixLink('/images/logo/logo_ninjaconcept_horizontal.svg')}
            style={{ cursor: 'pointer' }}
            onClick={() => (window.location.pathname = '/')}
            alt="Ninja Concept logo"
          />
        </Column>
        <Column justifyContent="flex-end" hideUntil={min1024} sizes={{ [min1024]: 9 }}>
          <Section
            row
            flex={1}
            width="100%"
            alignSelf="stretch"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Navigation bold color="white" onClick={() => scrollTo('swr')}>
              PROJEKTE
            </Navigation>
            <Spacer size={40} />
            <Navigation bold color="white" onClick={() => scrollTo('development')}>
              FULL STACK ENTWICKLUNG
            </Navigation>
            <Spacer size={40} />
            <Navigation bold color="white" onClick={() => scrollTo('ourAgency')}>
              AGENTUR
            </Navigation>
            <Spacer size={40} />
            <Navigation bold color="white" onClick={() => scrollTo('contact')}>
              KONTAKT
            </Navigation>
          </Section>
        </Column>
      </Row>
    </Container>
  </Page>
)
// --- HERO ---
const Intro = ({ size, offset, duration }) => (
  <Page
    id="intro"
    bgColor={colors.background}
    height="100vh"
    style={{
      position: 'fixed',
      zIndex: 20,
      transformStyle: 'preserve-3d',
      pointerEvents: 'none',
      transition: '500ms all linear'
    }}
  >
    <svg
      width={size + 8}
      height={size + 8}
      id="ninja-logo"
      style={{
        position: 'absolute',
        transition: '500ms all linear',
        zIndex: 30,
        opacity: 0.0,
        margin: 'auto',
        paddingRight: 15,
        left: 0,
        right: 0,
        top: 0,
        bottom: 210
      }}
      x="0px"
      y="0px"
      viewBox="0 0 200 168"
    >
      <path
        fill="white"
        d="M197.5,91.2c-0.4,0.2-1.2,0.5-2.3,1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.5
            c-0.8,0.3-1.6,0.7-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.6c-0.8,0.4-1.6,0.7-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3
            c-1.8-0.9-1.5-2.6-1.2-3.6c-0.8,0.4-1.7,0.7-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.5-2.6-1.2-3.6c-0.8,0.4-1.7,0.8-2.5,1.1
            c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.6c-0.8,0.4-1.7,0.7-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.5-2.6-1.2-3.6
            c-0.8,0.4-1.7,0.8-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.5-2.6-1.2-3.6c-0.9,0.4-1.7,0.8-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3
            c-1.8-0.9-1.6-2.6-1.2-3.6c-0.9,0.4-1.7,0.8-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.6c-0.9,0.4-1.7,0.8-2.5,1.1
            c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.5c-0.9,0.4-1.8,0.8-2.5,1.1c-1.2,1.8-0.5,4.3-0.5,4.3c-1.8-0.9-1.6-2.6-1.2-3.5
            c-1,0.5-1.6,0.7-1.6,0.7c-0.7,0.4-1.4,0.7-2.1,0.7c-1.1,0-2.2-2.1-2.9-2.1c-0.4,0-1.1,0.7-1.4,1.1c0.7,0.7,1.4,1.5,1.8,2.2
            c-3.2,1.4-9.3,4.3-10,4.3c-1.1,0-4.3-2.2-6.1-3.2c32.7-8.3,51-33.8,51-56.7c0-39.1-39.5-58.6-68.6-58.6c-26.6,0-62.8,14-68.5,52.8
            c-0.7,5-0.5,11.5,0.2,12.3c-1.6-1.6-5.6-4.1-9.2-4.1c-6.1,0-11.5,6.1-18.7,6.1c-4.3,0-10.4-2.2-15.1-6.1c-0.2,4.3,4,12.6,4.7,13.3
            c1.1,1.4,5.8,2.5,7.2,2.5c11.5,0,14.3-9.3,23.7-9.3c2.5,0,6.8,1.1,9,5h-1.8c-8.3,0-9.7,6.1-18.3,6.1c-1.1,0-2.5,0-3.6-0.4
            c0.7,1.5,3.6,5.8,5.4,5.8c6.5,0,10.8-10.4,18.7-9.7c0.8,1.1,1.1,2.1,1.5,3.5c5.7,17.6,30.7,34.1,47.5,36.9
            c0.7,0.1-14.1,8.4-14.1,8.4c0.6,0.9,1.5,2,2.1,2.5c-7.6,5.1-8.1,13.2-8.2,18.5c-0.7,0.7-8,1.7-8,5.3c0,1.4,1,3.1,2.8,3.2
            c0.7,0,6-2.6,7.1-2.6c0.6,0.8,2.1,3.4,5.6,6.2c0,0.8-1.5,3.6-2.2,4.7c-4.3,0.7-14.4,1.8-14,3.9c0.4,2.1,7.9,2.5,10.4,2.5
            s17.1-2,24.4-2.1c7.3-0.2,12.8-0.3,22.6,0c9.8,0.3,21.3,2.1,24.4,2.1s10.3-0.8,10.4-2.9c0.1-2.1-9.3-3.2-14-3.5
            c-0.7-1.5-2.1-3.9-2.1-4.5c2-1.1,4.4-2.2,6.4-8.4c0.6-1.9,0.9-3.3,1.1-5.8c0-6.4-2.2-10.7-8.3-16.9c0.7-0.7,1.7-1.7,2.1-2.4
            c-0.3-0.3-0.7-0.4-1.1-0.8c1.8-1.1,4.7-2.5,5.8-2.5c0.7,0,0.7,0.4,0.7,0.4v2.2c0.8-0.1,1.4-0.3,1.8-0.4c0-1.4,0-2.5,0.4-3.5
            c10.8-4.7,21.5-9.7,31.9-14.4c2.2-1.1,5.1-2.1,7.2-3.2c3.6-1.5,10.8-5.1,10.8-5.1S198.3,90.8,197.5,91.2z M55.3,85.8
            c10.8-5.5,26-8,56-7.6c29.9,0,46,2,56,7.2c-16.5,11.5-38.4,16.9-56,16.9C93.7,102.3,71.8,97.3,55.3,85.8z M87.6,160.1v-1.8
            c15.8-0.1,13.7-18.7,23.3-18.7c10.7,0,6.2,17.6,23.7,18.7c0.1,0.2,0.4,1.1,0.4,1.8H87.6z"
      />
      <path
        fill="white"
        d="M120.4,97.1c0.2,0.1,0.7,0,0.9-0.1c5.9-5,13.6-6.8,20.4-9.4c0.7-0.2,1-0.9,0.7-1.3c-0.3-0.4-1.7-0.1-1.7-0.1
            s-21.2,4.3-21.2,9.7C119.5,96.4,119.6,96.9,120.4,97.1z"
      />
      <path
        fill="white"
        d="M81.3,86.2c0,0-1.4-0.3-1.7,0.1c-0.3,0.4,0,1.1,0.7,1.3C87,90.1,94.7,92,100.6,97c0.2,0.2,0.7,0.2,0.9,0.1
            c0.8-0.2,0.9-0.7,0.9-1.3C102.4,90.4,81.3,86.2,81.3,86.2z"
      />
    </svg>
  </Page>
)

const Post = ({ data, isLast }, { width, isRetina }) => (
  <Column widths={{ [min768]: '33%' }}>
    {data.preview ? (
      <Column onClick={() => (window.location.href = data.url)}>
        <Spacer size={0} sizes={{ [min768]: 32 }} />
        <img
          src={`${prefixLink(data.preview) + (isRetina ? '@2px' : '')}.jpg`}
          width={(width < 768 && width) || '100%'}
          style={{ cursor: 'pointer' }}
          height="auto"
        />
      </Column>
    ) : null}
    <Column onClick={() => (window.location.href = data.url)}>
      <Spacer size={20} />
      <Heading3
        ellipsis
        style={{
          textAlign: 'left',
          alignSelf: 'flex-start',
          cursor: 'pointer'
        }}
      >
        {data.title}
      </Heading3>
    </Column>
    <Spacer size={20} />
    <Column>
      <Section center flex="1 0 auto" row style={{ alignSelf: 'flex-start' }}>
        <Icon
          width={50}
          height={50}
          icon={prefixLink(`/images/avatar/avatar_${data.avatar}.png`)}
          onClick={() => (window.location.href = `https://medium.com/@${data.medium}`)}
          style={{ cursor: 'pointer' }}
        />
        <Spacer size={20} />
        <Section flex="1 0 auto" alignSelf="center">
          <Paragraph
            flex
            style={{
              color: colors.highlight,
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={() => (window.location.href = `https://medium.com/@${data.medium}`)}
          >
            {data.author}
            <br />
          </Paragraph>
          <Section>
            <Paragraph style={{ textAlign: 'left' }}>
              {data.date} • {data.length}
            </Paragraph>
          </Section>
        </Section>
      </Section>
    </Column>
    <Column style={{ alignItems: 'flex-start' }}>
      <Spacer size={14} />
      <a href={data.url}>
        <Paragraph style={{ textAlign: 'left' }} color={colors.highlight}>
          read more ...
        </Paragraph>
      </a>
    </Column>
    <Column>
      <Spacer size={50} sizes={{ [min768]: 0 }} />
    </Column>
  </Column>
)

Post.contextTypes = {
  width: PropTypes.number,
  isRetina: PropTypes.bool
}

const LandingPage = (props, { width, retina }) => (
  <FelaProvider>
    <Intro size={140} duration={600} />
    <Navigator />
    <Helmet title="Ninja Concept | Web &amp; mobile applications" meta={metaData} />
    <Page
      data-scroll-scope="landing"
      bgCover
      bgRetina={retina}
      // bgImage={prefixLink('/images/images/hero_bg_image')}
      // bgImages={{
      //   [min360]: min360,
      //   [min480]: min480,
      //   [min768]: min768,
      //   [min1024]: min1024,
      //   [min1440]: min1440
      // }}
      style={{
        position: 'fixed',
        minHeight: '100vh',
        height: '100%'
      }}
    >
      <Container color="white" flex="1 0 auto">
        <Section flex="1 0 auto" center>
          <Row
            columns={{
              [min768]: 10,
              [min1024]: 8,
              [min1440]: 6
            }}
          >
            <Column>
              <Spacer
                size={32}
                sizes={{
                  [min480]: 48,
                  [min768]: 0
                }}
              />
              <Heading1 textAlign="center" color={colors.highlight}>
                Sophisticated Web & Mobile Applications
              </Heading1>
            </Column>
          </Row>
          <Spacer
            size={11}
            sizes={{
              [min480]: 14,
              [min768]: 18,
              [min1440]: 24
            }}
          />
          <Row
            columns={{
              [min1024]: 10,
              [min1920]: 8
            }}
          >
            <Column>
              <Subtext textAlign="center">
                Wir entwickeln skalierbare <b>IoT -, Full Stack - und Web/Mobile</b> Lösungen mittels aktuellster Software Stacks.
              </Subtext>
              <Spacer
                size={30}
                sizes={{
                  [min768]: 48,
                  [min1024]: 54,
                  [min1920]: 60
                }}
              />
              <Section center flex alignSelf="stretch">
                {/* <Spacer
                  size={28}
                  sizes={{
                    [min768]: 20,
                    [min1024]: 28,
                    [min1024]: 24
                  }}
                /> */}
                <Button
                  onClick={() => scrollTo('stylight')}
                  big
                  color="white"
                  activeColor="white"
                  activeFontColor={colors.highlight}
                  width={260}
                  text="LEARN MORE .."
                />
              </Section>
            </Column>
          </Row>
        </Section>
        <Section center flex="0 0 50px" alignSelf="stretch">
          <Icon
            icon={prefixLink('/images/images/scroll_down.svg')}
            width={21}
            height={11}
            style={{ animation: 'upDown 700ms linear 0s infinite' }}
          />
        </Section>
      </Container>
    </Page>
    {}
    <Page
      style={{
        position: 'absolute',
        top: '100%',
        padding: 0
      }}
    >
      <Page data-scroll-scope="nullLeasing" bgColor={colors.backgroundnullLeasing}>
        <Spacer
          size={60}
          sizes={{
            [min768]: 80,
            [min1440]: 120,
            [min1920]: 120
          }}
        />
        <Container center flex="1 0 auto">
          <Row center columns={{ [min768]: 12 }}>
            <Column
              center
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.1,
                [min768]: 0.1
              })}
            >
              <Subtext textAlign="center">Einige unserer neuesten Projekte</Subtext>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 16,
                  [min1024]: 16,
                  [min1440]: 16
                }}
              />
              <Line data-parallax-line="135px" color={colors.text} />
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 120,
              [min1024]: 160
            }}
          />
          <Row
            center
            columns={{
              [min768]: 12,
              [min1920]: 10
            }}
          >
            <Column
              sizes={{
                [min1024]: 6,
                [min1440]: 4,
                [min1920]: 5
              }}
              order={3}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.4,
                [min1024]: 0.17,
                [min1440]: 0.3,
                [min1920]: 0.4
              })}
            >
              <Heading1>Null-Leasing.com</Heading1>
              <Spacer
                size={17}
                sizes={{
                  [min768]: 8,
                  [min1024]: 24,
                  [min1440]: 20
                }}
              />
              <Paragraph>
                Das Vergleichsportal für Leasing-Fahrzeuge verschiedenster Automarken bietet eine
                Übersicht über die günstigsten Leasingangebote von autorisierten Vertragshändlern
                aus ganz Deutschland. Umfangreiche Such- und Filtermöglichkeiten unterstützen Sie
                dabei, Ihr Traumauto zu finden. Den Relaunch der Null-Leasing.com Website haben wir
                von A bis Z begleitet.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Paragraph lora={false}>
                (Elixir/Phoenix, React, Node/Next JS, GraphQL,
                Elasticsearch / MongoDB, Tailwind&nbsp;CSS / PostCSS, Netlify, Webpack)
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Services>// Entwicklung // SEO Optimierung</Services>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Section alignItems="flex-start">
                <Button
                  color={colors.text}
                  hoverColor={colors.highlightnullLeasing}
                  activeColor={colors.highlightnullLeasing}
                  activeFontColor="white"
                  text={
                    <a
                      href="https://www.null-leasing.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Webseite besuchen
                    </a>
                  }
                />
              </Section>
            </Column>
            <Column
              noPadding
              justifyContent="center"
              sizes={{
                [min1024]: 6,
                [min1920]: 5
              }}
              order={width >= min1024 ? 5 : 1}
            >
              <Image
                base={prefixLink('/images/images/nl_site_responsive')}
                sizes={[min768, min1024, min1440]}
                data-scroll-effect="fadeInUp 500ms"
                data-scroll-offset={JSON.stringify({
                  0: 0.2,
                  [min768]: 0.4,
                  [min1024]: 0.15,
                  [min1440]: 0.3,
                  [min1920]: 0.4
                })}
                style={{
                  position: width >= min1024 && 'absolute',
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
              />
              <Spacer
                sizes={{
                  [min768]: 40,
                  [min1024]: 0
                }}
              />
            </Column>
            <Spacer
              size={39}
              sizes={{
                [min480]: 34,
                [min768]: 0
              }}
              style={{ order: 2 }}
            />
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 100,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
      </Page>

      <Page data-scroll-scope="stylight" bgColor="white">
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160
          }}
        />
        <Container center flex="1 0 auto">
          <Row
            center
            columns={{
              [min768]: 12,
              [min1920]: 10
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.3,
              [min768]: 0.3
            })}
          >
            <Column
              noPadding
              justifyContent="center"
              sizes={{
                [min1024]: 6,
                [min1920]: 5
              }}
              data-parallax-speed={0.2}
              data-parallax-offset={0.8}
            >
              <Image
                jpg
                sizes={[min768, min1440, min1920]}
                base={prefixLink('/images/images/stylight_app')}
                style={{
                  position: width >= min1024 && 'absolute',
                  overflow: 'hidden',
                  left: width >= min1440 ? '-75px' : width >= min1024 ? '-30px' : 'auto',
                  pointerEvents: 'none'
                }}
              />
              <Spacer
                sizes={{
                  [min768]: 40,
                  [min1024]: 0
                }}
              />
            </Column>

            <Spacer
              size={39}
              sizes={{
                [min480]: 34,
                [min768]: 0
              }}
            />
            <Column
              justifyContent="center"
              sizes={{
                [min1024]: 6,
                [min1920]: 5
              }}
              data-parallax-scope="stylight"
            >
              <Heading1>Stylight</Heading1>
              <Spacer
                size={17}
                sizes={{
                  [min768]: 8,
                  [min1024]: 24,
                  [min1440]: 20
                }}
              />
              <Paragraph>
                Mit einer Auswahl aus über 230 Online-Shops vereint die Stylight App alle Styles aus
                den Bereichen Mode, Wohnen und Beauty und liefert täglich Inspirationen zu den
                neuesten Trends. Dabei ist die Stylight App eine App, mit der man mehr kann als nur
                shoppen - Wunschlisten anlegen, suchen und vergleichen von Produkten, hilfreiche
                Tipps in Magazinbeiträgen finden uvm.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Paragraph bold lora={false}>
                Hybrid Web Mobile App für iOS und Android - Tracking, Attribution, Push
                Notifications.
              </Paragraph>
              <Paragraph lora={false}>(React Native)</Paragraph>

              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Services>// Beratung // Entwicklung // App Store Deployments</Services>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Section row={width >= min768}>
                <IconLink
                  href="https://itunes.apple.com/de/app/stylight/id648471423?mt=8"
                  icon={prefixLink('/images/app_store_badges/apple_app_store_download_badge.svg')}
                  width={175}
                  height={52}
                />
                <IconLink
                  href="https://play.google.com/store/apps/details?id=com.stylight.app"
                  icon={prefixLink('/images/app_store_badges/google-play-badge.svg')}
                  width={175}
                  height={52}
                  style={{
                    marginLeft: width >= min768 ? 16 : 0,
                    marginTop: width < min768 ? 16 : 0
                  }}
                />
              </Section>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
      </Page>

      <Page data-scroll-scope="kymo" bgColor={colors.backgroundkymo}>
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
        <Container center flex="1 0 auto">
          <Row
            center
            columns={{
              [min768]: 12,
              [min1920]: 10
            }}
          >
            <Column
              sizes={{
                [min1024]: 6,
                [min1440]: 4,
                [min1920]: 5
              }}
              order={3}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.4,
                [min1024]: 0.17,
                [min1440]: 0.3,
                [min1920]: 0.4
              })}
            >
              <Heading1>kymo</Heading1>
              <Spacer
                size={17}
                sizes={{
                  [min768]: 8,
                  [min1024]: 24,
                  [min1440]: 20
                }}
              />
              <Paragraph>
                Für unsere Freunde von kymo haben wir den Relaunch der Brand Website realisiert.
                kymo setzt u.a. beim Inbound Marketing auf das Content Management System HubSpot und
                so haben wir die komplexen Designs der neuen kymo Website in das HubSpot CMS
                implementiert.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Services>
                // Entwicklung // Content Marketing & SEO Consulting // Backend Systeme (Migration,
                Integration)
              </Services>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Section alignItems="flex-start">
                <Button
                  color={colors.text}
                  hoverColor={colors.highlightkymo}
                  activeColor={colors.highlightkymo}
                  activeFontColor="white"
                  text={
                    <a href="https://www.kymo.de/de/" target="_blank" rel="noopener noreferrer">
                      Webseite besuchen
                    </a>
                  }
                />
              </Section>
            </Column>
            <Column
              noPadding
              justifyContent="center"
              sizes={{
                [min1024]: 6,
                [min1920]: 5
              }}
              order={width >= min1024 ? 5 : 1}
            >
              <Image
                base={prefixLink('/images/images/kymo_site_responsive')}
                sizes={[min768, min1024, min1440]}
                data-scroll-effect="fadeInUp 500ms"
                data-scroll-offset={JSON.stringify({
                  0: 0.2,
                  [min768]: 0.4,
                  [min1024]: 0.15,
                  [min1440]: 0.3,
                  [min1920]: 0.4
                })}
                style={{
                  position: width >= min1024 && 'absolute',
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
              />
              <Spacer
                sizes={{
                  [min768]: 40,
                  [min1024]: 0
                }}
              />
            </Column>
            <Spacer
              size={39}
              sizes={{
                [min480]: 34,
                [min768]: 0
              }}
              style={{ order: 2 }}
            />
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 100,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
      </Page>

      <Page data-scroll-scope="swr" bgColor={colors.backgroundswr}>
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
        <Container center color="white" flex="1 0 auto">
          <Row
            columns={{
              [min768]: 12,
              [min1440]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.3,
              [min768]: 0.3
            })}
          >
            <Column
              justifyContent="center"
              alignItems={
                width >= min1920 ? 'flex-start' : width >= min768 ? 'center' : 'flex-start'
              }
              sizes={{
                [min768]: 4,
                [min1920]: 3
              }}
              data-parallax-speed={0.2}
              data-parallax-offset={0.8}
            >
              <Image
                base={prefixLink('/images/images/swr2-app')}
                sizes={[min768, min1440, min1920]}
              />
            </Column>
            <Spacer
              size={39}
              sizes={{
                [min480]: 34,
                [min768]: 0
              }}
            />
            <Column
              justifyContent="center"
              sizes={{
                [min768]: 8,
                [min1440]: 6,
                [min1920]: 6
              }}
              data-parallax-scope="swr"
            >
              <Heading1>SWR2</Heading1>
              <Spacer
                size={17}
                sizes={{
                  [min768]: 8,
                  [min1024]: 24,
                  [min1440]: 20
                }}
              />
              <Paragraph>
                Hören Sie SWR2 live, zeitversetzt, überall, wo Sie online sind. Den Zeitpunkt, ins
                laufende Programm einzusteigen, bestimmen Sie selbst.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Paragraph bold lora={false}>
                iOS und Android App mit umfangreichen Streaming und Download Funktionen.
              </Paragraph>
              <Paragraph lora={false}>
                (React Native, GraphQL, Node.js, Google Cloud, Firebase Cloud Messaging)
              </Paragraph>

              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Services>// Workshops // Design // Entwicklung // Betreuung</Services>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Section row={width >= min768}>
                <IconLink
                  href="https://itunes.apple.com/de/app/swr2-radio/id814462854?mt=8"
                  icon={prefixLink('/images/app_store_badges/apple_app_store_download_badge.svg')}
                  width={175}
                  height={52}
                />
                <IconLink
                  href="https://play.google.com/store/apps/details?id=de.swr.swr2radio"
                  icon={prefixLink('/images/app_store_badges/google-play-badge.svg')}
                  width={175}
                  height={52}
                  style={{
                    marginLeft: width >= min768 ? 16 : 0,
                    marginTop: width < min768 ? 16 : 0
                  }}
                />
              </Section>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
      </Page>
      {/* <Page data-scroll-scope="clockson" bgColor={colors.backgroundclockson}>
        <Spacer
          size={60}
          sizes={{
            [min768]: 80,
            [min1440]: 120,
            [min1920]: 120
          }}
        />
        <Container center color="white" flex="1 0 auto">
          <Row center columns={{ [min768]: 12 }}>
            <Column
              center
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.1,
                [min768]: 0.1
              })}
            >
              <Subtext textAlign="center">Einige unserer neuesten Projekte</Subtext>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 16,
                  [min1024]: 16,
                  [min1440]: 16
                }}
              />
              <Line data-parallax-line="135px" color="#ACFEEC" />
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 120,
              [min1920]: 160
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1440]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.3,
              [min768]: 0.3
            })}
          >
            <Column
              justifyContent="center"
              alignItems={
                width >= min1920 ? 'flex-start' : width >= min768 ? 'center' : 'flex-start'
              }
              sizes={{
                [min768]: 4,
                [min1920]: 3
              }}
              data-parallax-speed={0.2}
              data-parallax-offset={0.8}
            >
              <Image
                base={prefixLink('/images/images/clockson_iphone')}
                sizes={[min768, min1440, min1920]}
              />
            </Column>
            <Spacer
              size={39}
              sizes={{
                [min480]: 34,
                [min768]: 0
              }}
            />
            <Column
              justifyContent="center"
              sizes={{
                [min768]: 8,
                [min1440]: 6,
                [min1920]: 5
              }}
              data-parallax-scope="clock"
            >
              <Heading1>Clockson</Heading1>
              <Spacer
                size={17}
                sizes={{
                  [min768]: 8,
                  [min1024]: 24,
                  [min1440]: 20
                }}
              />
              <Paragraph>
                Eine native, gestengesteuerte iOS-App, die das Zeiterfassungstool mite um ein
                schnelles Time Tracking unterwegs erweitert.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1024]: 36
                }}
              />
              <Paragraph bold lora={false}>
                Die neue Version ist bald verfügbar.
              </Paragraph>
              <Spacer
                size={36}
                sizes={{
                  [min768]: 24,
                  [min1440]: 28
                }}
              />
              <Services>// Konzept // Design // Entwicklung // Distribution</Services>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
      </Page> */}
      { }
      <Page data-scroll-scope="mentalAktiv" bgColor="white">
        <Spacer
          size={80}
          sizes={{
            [min480]: 120,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
        <Container center flex="1 0 auto">
          <Section flex center>
            <Row
              columns={{
                [min768]: 12,
                [min1920]: 10
              }}
            >
              <Column
                sizes={{
                  [min768]: 6,
                  [min1440]: 4,
                  [min1920]: 5
                }}
                order={3}
                data-scroll-effect="fadeInUp 500ms"
                data-scroll-offset={JSON.stringify({
                  0: 0.2,
                  [min768]: 0.4,
                  [min1024]: 0.17,
                  [min1440]: 0.3,
                  [min1920]: 0.4
                })}
              >
                <Heading1>Mental-aktiv.de</Heading1>
                <Spacer
                  size={17}
                  sizes={{
                    [min768]: 8,
                    [min1024]: 24,
                    [min1440]: 20
                  }}
                />
                <Paragraph>
                  Eine Knobel- und Denksport-Community mit über 10.000 Mitgliedern, die sich dank
                  einheitlichem Design in die Markenwelt des Kunden einfügt. Die gemeinsame
                  Codebasis ermöglicht interaktive Echtzeit-Applikationen für Web, iOS und Android.
                </Paragraph>
                <Spacer
                  size={36}
                  sizes={{
                    [min768]: 24,
                    [min1024]: 36
                  }}
                />
                <Services>// Konzept // Design // Entwicklung // Betrieb</Services>
                <Spacer
                  size={36}
                  sizes={{
                    [min768]: 24,
                    [min1440]: 28
                  }}
                />
                <Section alignItems="flex-start">
                  <Button
                    color={colors.text}
                    hoverColor={colors.highlightmental}
                    activeColor={colors.highlightmental}
                    activeFontColor="white"
                    text={
                      <a
                        href="https://www.mental-aktiv.de/home"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Webseite besuchen
                      </a>
                    }
                  />
                </Section>
              </Column>
              <Column
                sizes={{
                  [min768]: 4,
                  [min1920]: 5
                }}
                order={width >= min768 ? 5 : 1}
              >
                <Image
                  base={prefixLink('/images/images/mental_aktiv_macbook')}
                  sizes={[min768, min1920]}
                  data-scroll-effect="fadeInUp 500ms"
                  data-scroll-offset={JSON.stringify({
                    0: 0.2,
                    [min768]: 0.4,
                    [min1024]: 0.15,
                    [min1440]: 0.3,
                    [min1920]: 0.4
                  })}
                  style={{
                    position: width >= min768 && 'absolute',
                    overflow: 'hidden',
                    right: getMacbookRight(width),
                    marginTop: getMacbookTop(width),
                    pointerEvents: 'none'
                  }}
                />
              </Column>
              <Spacer
                size={39}
                sizes={{
                  [min480]: 34,
                  [min768]: 0
                }}
                style={{ order: 2 }}
              />
            </Row>
          </Section>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 100,
            [min768]: 240,
            [min1024]: 320,
            [min1440]: 360,
            [min1920]: 360
          }}
        />
      </Page>
      { }
      <Page data-scroll-scope="development" bgColor={colors.background}>
        <Spacer
          size={80}
          sizes={{
            [min480]: 100,
            [min768]: 160,
            [min1440]: 200,
            [min1920]: 240
          }}
        />
        <Container flex="1 0 auto" color="white">
          <Row
            columns={{
              [min768]: 10,
              [min1920]: 8
            }}
          >
            <Column
              center
              sizes={{
                [min768]: 10,
                [min1920]: 8
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Heading1>We Love to Develop</Heading1>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 19,
                  [min1024]: 22,
                  [min1440]: 24
                }}
              />
              <Line data-parallax-line="135px" color={colors.highlight} />
              <Spacer
                size={20}
                sizes={{
                  [min768]: 27,
                  [min1024]: 32,
                  [min1440]: 30
                }}
              />
              <Paragraph textAlign="center">
                Wir entwickeln maßgeschneiderte Software jeglicher Komplexität. Schnell und
                zuverlässig. Dabei verwenden wir State-of-the-Art Technologien und moderne funktionale
                Entwicklungsparadigmen.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 120,
              [min1920]: 120
            }}
          />
          <Row
            columns={{
              [min768]: 10,
              [min1920]: 8
            }}
          >
            <Column
              center
              sizes={{
                [min768]: 10,
                [min1920]: 8
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Heading2 color={colors.highlight}>Full-Stack Development</Heading2>
              <Spacer size={8} sizes={{ [min768]: 4 }} />

              <Paragraph textAlign="center">
                Die Entwicklung moderner Client-Anwendungen bringt besondere Herausforderungen mit
                sich. In einer Welt, die täglich neue Standards hervorbringt, sehen wir es als
                unsere Aufgabe, die wegweisenden Trends zu erkennen.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/react')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>React / React Native</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                React gilt seit langem als der führende Standard zur Entwicklung dynamischer
                Benutzerschnittstellen im Web. React Native ist für uns die Zukunft
                effizienter, nativer cross-platform Mobile Entwicklung.
              </Paragraph>
            </Column>
          </Row>
          <Spacer size={120} sizes={{ [min768]: 100 }} />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              center
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
            >
              <Image base={prefixLink('/images/technologielogos/elm')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>Elm</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Elm ist eine bezaubernde Sprache für extrem zuverlässige Web-Apps mit hervorragender
                Performance und der Garantie, Laufzeit-fehlerfreien Javascript Code zu generieren.
                Wir sind begeistert!
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 10,
              [min1920]: 8
            }}
          >
            <Column
              center
              sizes={{
                [min768]: 10,
                [min1920]: 8
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph textAlign="center">
                Skalier- und wartbare Microservices und APIs bilden das Rückgrat Ihres Systems.
                Aufgrund unserer langjährigen Erfahrung mit etablierten und aktuellen Frameworks
                bleiben wir stets objektiv bei der Auswahl der richtigen Basis für Ihr Produkt.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/phoenix')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>Elixir / Phoenix</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Elixir und Erlang/OTP ist eine der weltweit
                robustesten Plattformen für hochverfügbare Systeme. Phoenix ist ein modernes
                Concurrency-Web-Framework ohne Kompromisse bezüglich Produktivität, Skalierbarkeit und
                Wartbarkeit.
              </Paragraph>
            </Column>
            { }
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/ruby')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>Ruby / Ruby on Rails</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Ruby's Ausdrucksfähigkeit ist großartig, um Business-Logiken sauber umzusetzen. Das
                riesige Ökosystem erlaubt eine sehr nachhaltige Produktentwicklung bei gleichzeitig
                hoher Codequalität. In den letzten Jahren entwickelten wir umfangreiche
                Systeme in Ruby. Wir helfen gerne bei der Migration auf skalierbarere Plattformen wie Elixir.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 120,
              [min1920]: 120
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/meteor')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>Node.js</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Die Node.js Runtime ist natürlich auch für uns essentiell zur effizienten
                Entwicklung moderner Full-Stack-Anwendungen.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/graphql')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>GraphQL</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                GraphQL bildet, als flexible API-Schnittstelle, die perfekte Brücke zwischen Backend
                und Frontend. Das mittlerweile große Ecosystem mit innovativen Services wie
                GraphCool/Prisma oder GraphCMS ist für uns ein echter Game-Changer bezüglich
                effizienter Projekt Entwicklung.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
        </Container>
        <Spacer
          size={100}
          sizes={{
            [min1024]: 160,
            [min1440]: 160
          }}
        />
      </Page>
      {/* <Image
        style={{ width: '100vw' }}
        excludeMin
        jpg
        base={prefixLink('/images/images/dev')}
        sizes={[min360, min480, min768, min1024, min1440]}
      />
      <Page bgColor={colors.background}>
        <Spacer
          size={85}
          sizes={{
            [min1024]: 105,
            [min1440]: 105,
            [min1440]: 105
          }}
        />
        <Container flex="1 0 auto" color="white">
          <Row
            columns={{
              [min768]: 10,
              [min1920]: 8
            }}
          >
            <Column
              center
              sizes={{
                [min768]: 10,
                [min1920]: 8
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.1,
                [min768]: 0.1
              })}
            >
              <Heading2 color={colors.highlight}>Frontend Development</Heading2>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph textAlign="center">
                Die Entwicklung moderner Client-Anwendungen bringt besondere Herausforderungen mit
                sich. In einer Welt, die täglich neue Standards hervorbringt, sehen wir es als
                unsere Aufgabe die wegweisenden Trends zu erkennen.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 100,
              [min1440]: 100,
              [min1920]: 100
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              rowOnSmall
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
              center
            >
              <Image base={prefixLink('/images/technologielogos/react')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>React / Redux / GraphQL</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Die Kombination aus React und Redux ist der führende Standard zur Entwicklung
                dynamischer Benutzerschnittstellen. GraphQL bildet als flexible API-Schnittstelle
                die perfekte Brücke zwischen Backend und Frontend.
              </Paragraph>
            </Column>
          </Row>
          <Spacer size={120} sizes={{ [min768]: 100 }} />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              center
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
            >
              <Image base={prefixLink('/images/technologielogos/elm')} />
            </Column>
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Heading3>Elm</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Elm ist eine bezaubernde Sprache für extrem zuverlässige Web-Apps, mit
                hervorragender Performance und der Garantie Laufzeit-fehlerfreien Javascript Code zu
                generieren. Wir sind begeistert!
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={40}
            sizes={{
              [min768]: 60,
              [min1440]: 80
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1920]: 8
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              alignItems="center"
              sizes={{
                [min768]: 3,
                [min1024]: 2,
                [min1920]: 2
              }}
            />
            <Spacer size={28} sizes={{ [min768]: 0 }} />
            <Column
              sizes={{
                [min768]: 9,
                [min1024]: 8,
                [min1920]: 6
              }}
            >
              <Section alignItems="center">
                <a href="mailto:contact@ninjaconcept.com">
                  <Button
                    color="white"
                    hoverColor={colors.highlight}
                    activeColor={colors.highlight}
                    activeFontColor={colors.text}
                    text="Kontaktieren Sie uns"
                  />
                </a>
              </Section>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min768]: 160,
            [min1024]: 200,
            [min1920]: 240
          }}
        />
      </Page> */}
      { }
      {/* <Page data-scroll-scope="UIUX" bgColor="white">
        <Container flex="1 0 auto">
          <Spacer
            size={80}
            sizes={{
              [min768]: 200,
              [min1440]: 160,
              [min1920]: 240
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8
            }}
          >
            <Column
              center
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Heading1>Design is our Passion</Heading1>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 19,
                  [min1024]: 22,
                  [min1440]: 24
                }}
              />
              <Line data-parallax-line="135px" color={colors.highlight} />
              <Spacer
                size={20}
                sizes={{
                  [min768]: 27,
                  [min1024]: 32,
                  [min1440]: 24
                }}
              />
              <Quote textAlign="center">Think more, design less.</Quote>
              <Spacer
                size={6}
                sizes={{
                  [min768]: 6,
                  [min1024]: 6,
                  [min1440]: 6
                }}
              />
              <QuoteAuthor color="red">
                - Ellen Lupton, US-Amerikanische Autorin und Grafik-Designerin
              </QuoteAuthor>
            </Column>
          </Row>
          <Spacer size={60} />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Paragraph textAlign="center">
                Wir designen professionelle User Interfaces, die Ihre Kunden begeistern und Ihren
                Business-Zielen entsprechen. Dabei werden Sie von Beginn an in den Design- und
                Entwicklungsprozess einbezogen.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 120,
              [min1440]: 120,
              [min1920]: 120
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.3,
                [min768]: 0.3
              })}
            >
              <Heading3>Research & Strategie</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Der Design-Prozess beginnt mit User-Research damit wir ein Verständnis für die
                Bedürfnisse und Probleme Ihrer Zielgruppe bekommen und ungenutzte Potenziale
                aufdecken können.
                <br />
                <br />
                Wir setzen auf strategische und kreative Methoden wie Lean Design, Design Thinking
                und Google Ventures Design Sprints und definieren gemeinsam mit Ihnen das Ziel des
                Projekts.
              </Paragraph>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={120}
          sizes={{
            [min1024]: 140,
            [min1440]: 160
          }}
        />
      </Page> */}
      {/* <Image
        style={{ width: '100vw' }}
        excludeMin
        jpg
        base={prefixLink('/images/images/uiux')}
        sizes={[min360, min480, min1024, min1440]}
      /> */}
      {/* <Page bgColor="white">
        <Spacer
          size={80}
          sizes={{
            [min1024]: 105,
            [min1440]: 105
          }}
        />
        <Container flex="1 0 auto">
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.1,
                [min768]: 0.1
              })}
            >
              <Heading3>Prototyping</Heading3>
              <Spacer size={8} sizes={{ [min768]: 4 }} />
              <Paragraph>
                Angefangen von Skizzen mit Stift und Papier bis hin zu interaktiven Prototypen
                hauchen wir Ihren Ideen Leben ein. In iterativen Schritten und interdisziplinären
                Teams entstehen innovative Produkte, die technisch sowie emotional begeistern.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={50}
            sizes={{
              [min768]: 80,
              [min1440]: 80,
              [min1920]: 80
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8,
                [min1920]: 6
              }}
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.15,
                [min768]: 0.15
              })}
            >
              <Line width="100%" stroke={1} color="rgba(0, 0, 0, 0.2)" />
              <Spacer
                size={18}
                sizes={{
                  [min768]: 22,
                  [min1024]: 24,
                  [min1440]: 28
                }}
              />
              <Quote textAlign="center">We help your ideas get off the ground.</Quote>
              <Spacer
                size={18}
                sizes={{
                  [min768]: 22,
                  [min1024]: 24,
                  [min1440]: 28
                }}
              />
              <Line width="100%" stroke={1} color="rgba(0, 0, 0, 0.2)" />
            </Column>
          </Row>
          <Spacer
            size={50}
            sizes={{
              [min768]: 80,
              [min1440]: 80,
              [min1920]: 80
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.2,
                [min768]: 0.2
              })}
            >
              <Heading3>User Interface Design</Heading3>
              <Paragraph>
                Modernes Interface Design besticht nicht nur durch schöne Pixel, sondern auch durch
                Klarheit, und Einfachheit - und dies über sämtliche Devices hinweg. Mit einem
                stimmigen Visual Design, das Ihre Corporate Identity weiterführt, schaffen Sie
                Vertrauen und Verbundenheit Ihrer Kunden mit der Marke.
              </Paragraph>
            </Column>
          </Row>
          <Spacer
            size={60}
            sizes={{
              [min768]: 60,
              [min1440]: 80
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              data-scroll-effect="fadeInUp 500ms"
              data-scroll-offset={JSON.stringify({
                0: 0.15,
                [min768]: 0.15
              })}
            >
              <Section alignItems="center">
                <a href="mailto:contact@ninjaconcept.com">
                  <Button
                    color={colors.text}
                    hoverColor={colors.highlight}
                    activeColor={colors.highlight}
                    activeFontColor="white"
                    text="Kontaktieren Sie uns"
                  />
                </a>
              </Section>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={105}
          sizes={{
            [min768]: 185,
            [min1024]: 185,
            [min1920]: 225
          }}
        />
      </Page> */}
      { }
      <Image
        style={{ width: '100vw' }}
        excludeMin
        jpg
        base={prefixLink('/images/images/impressum')}
        sizes={[min360, min480, min768, min1024, min1440]}
      />
      <Page data-scroll-scope="ourAgency" bgColor={colors.background}>
        <Spacer
          size={50}
          sizes={{
            [min768]: 65,
            [min1440]: 105,
            [min1920]: 105
          }}
        />
        <Container center color="white" flex="1 0 auto">
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              center
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8,
                [min1920]: 6
              }}
            >
              <Heading1>Our Agency</Heading1>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 19,
                  [min1024]: 22,
                  [min1440]: 24
                }}
              />
              <Line data-parallax-line="20%" color={colors.highlight} />
            </Column>
          </Row>
          <Spacer
            size={40}
            sizes={{
              [min768]: 54,
              [min1024]: 62,
              [min1440]: 60
            }}
          />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8,
                [min1920]: 6
              }}
            >
              <Paragraph>
                Seit 2010 realisieren wir Projekte für Startups, mittelständische Unternehmen,
                Konzerne und Forschungseinrichtungen. Dazu arbeiten bei NinjaConcept Experten aus
                den Bereichen User Experience Design, Software Engineering, Mathematik, Datenanalyse
                und Cloud Operations zusammen. Die kontinuierliche Weiterentwicklung im Team sowie
                eine offene und frische Herangehensweise an neue Herausforderungen bilden den Kern
                unserer Unternehmensphilosophie.
              </Paragraph>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={85}
          sizes={{
            [min480]: 85,
            [min768]: 85,
            [min1024]: 85,
            [min1440]: 85
          }}
        />
        <Container
          center
          color="white"
          flex="1 0 auto"
          data-scroll-effect="fadeInUp 500ms"
          data-scroll-offset={JSON.stringify({
            0: 0.2,
            [min768]: 0.2
          })}
        >
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8,
                [min1920]: 6
              }}
            >
              <Heading3 color={colors.highlight}>Join the Team!</Heading3>
            </Column>
          </Row>
          <Spacer size={20} sizes={{ [min1440]: 16 }} />
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
          >
            <Column
              sizes={{
                [min768]: 5,
                [min1024]: 4,
                [min1440]: 3
              }}
            >
              <a href="mailto:contact@ninjaconcept.com?subject=React%20Ninja">
                <LinkList>React/React Native Ninja</LinkList>
              </a>
              <a href="mailto:contact@ninjaconcept.com?subject=JS%20Full%20Stack%20Ninja">
                <LinkList>Node.js Full Stack Ninja</LinkList>
              </a>
            </Column>
            <Column
              sizes={{
                [min768]: 5,
                [min1024]: 4,
                [min1440]: 3
              }}
            >
              <a href="mailto:contact@ninjaconcept.com?subject=Ruby%20Elixir%20Ninja">
                <LinkList>Elixir Ninja</LinkList>
              </a>
              <a href="mailto:contact@ninjaconcept.com?subject=Elm%20Haskell%20Ninja">
                <LinkList>Elm / Haskell Ninja</LinkList>
              </a>
            </Column>
            <Column
              sizes={{
                [min768]: 2,
                [min1920]: 0
              }}
            />
          </Row>
          <Spacer
            size={80}
            sizes={{
              [min768]: 160,
              [min1440]: 160
            }}
          />
        </Container>
      </Page>
      <Page data-scroll-scope="blog" bgColor="white">
        <Spacer
          size={50}
          sizes={{
            [min768]: 65,
            [min1440]: 105,
            [min1920]: 105
          }}
        />
        <Container center color={colors.background} flex="1 0 auto">
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8,
              [min1920]: 6
            }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column
              center
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8,
                [min1920]: 6
              }}
            >
              <Heading1>The Ninja Blog</Heading1>
              <Spacer
                size={12}
                sizes={{
                  [min768]: 19,
                  [min1024]: 22,
                  [min1440]: 24
                }}
              />
              <Line data-parallax-line="20%" color={colors.highlight} />
            </Column>
          </Row>
          <Spacer
            size={40}
            sizes={{
              [min768]: 54,
              [min1024]: 62,
              [min1440]: 60
            }}
          />
          <Row
            columns={{ [min768]: 12 }}
            data-scroll-effect="fadeInUp 500ms"
            data-scroll-offset={JSON.stringify({
              0: 0.2,
              [min768]: 0.2
            })}
          >
            <Column sizes={{ [min768]: 12 }} center>
              <Subtext textAlign="center">
                Lesen Sie, was unsere Ninjas in letzter Zeit geschrieben haben.
              </Subtext>
            </Column>
          </Row>
        </Container>
        <Spacer size={80} sizes={{ [min768]: 60 }} />
        <Container color={colors.text} flex="1 0 auto">
          <Section flex="1 0 auto" width="100%" alignSelf="stretch">
            <Row columns={{ [min768]: 12 }} center>
              {posts.slice(0, 3).map(post => (
                <Post key={post.url} data={post} />
              ))}
            </Row>
          </Section>
        </Container>
        <Spacer
          size={30}
          sizes={{
            [min480]: 30,
            [min768]: 85,
            [min1024]: 85,
            [min1440]: 85
          }}
        />
        <Section alignItems="center" alignSelf="stretch">
          <RouterLink to={prefixLink('/blog/')}>
            <Button
              color={colors.text}
              hoverColor={colors.highlight}
              activeColor={colors.highlight}
              activeFontColor="white"
              text="Alle Blog Posts anzeigen"
            />
          </RouterLink>
        </Section>
        <Spacer
          size={85}
          sizes={{
            [min480]: 85,
            [min768]: 85,
            [min1024]: 110,
            [min1440]: 110
          }}
        />
      </Page>
      <Page height="auto" data-scroll-scope="testimonials" bgColor={colors.background}>
        <Spacer
          size={85}
          sizes={{
            [min480]: 85,
            [min768]: 85,
            [min1024]: 85,
            [min1440]: 85
          }}
        />
        <Container center color="white" flex="1 0 auto">
          <Row
            columns={{
              [min768]: 12,
              [min1024]: 10,
              [min1440]: 8
            }}
          >
            <Column
              sizes={{
                [min768]: 12,
                [min1024]: 10,
                [min1440]: 8
              }}
            >
              <Quote textAlign="center">
                NinjaConcept überzeugte uns durch ihre hervorragenden Entwickler-Skills. Wir wurden
                von der Konzeption bis zur Realisierung unseres Projektes sehr gut beraten. Eine
                tiefe Auseinandersetzung mit den Projektinhalten ermöglichte eine sehr effiziente
                Entwicklung. Danke für die tolle Zusammenarbeit in unserem Open Source Projekt!
              </Quote>
              <Spacer size={32} sizes={{ [min768]: 32 }} />
              <QuoteAuthor textAlign="center">
                Dr. Nicole Jung, Akademische Rätin, KIT (Karlsruhe Institute of Technology)
              </QuoteAuthor>
            </Column>
          </Row>
          <Spacer
            size={70}
            sizes={{
              [min768]: 90,
              [min1024]: 90,
              [min1440]: 110
            }}
          />
          <Row
            center
            columns={{
              [min768]: 12,
              [min1440]: 10
            }}
          >
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/stylight')} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/kymo')} sizes={[768, 1920]} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/swr')} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/billiger-de')} sizes={[768, 1920]} />
            </Column>

            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/deutsche_bank')} sizes={[768, 1920]} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image
                base={prefixLink('/images/kundenlogos/agilent_technologies')}
                sizes={[768, 1920]}
              />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/kit')} sizes={[768, 1920]} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image
                base={prefixLink('/images/kundenlogos/schwabe_dr_willmar_schwabe_arzneimittel')}
                sizes={[768, 1920]}
              />
            </Column>
            {/* <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/telefonica')} sizes={[768, 1920]} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/haufe')} sizes={[768, 1920]} />
            </Column> */}
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/lexware')} sizes={[768, 1920]} />
            </Column>
            <Column
              height={112}
              widths={{
                [min768]: '33%',
                [min1024]: '25%',
                [min1440]: '20%'
              }}
              center
            >
              <Image base={prefixLink('/images/kundenlogos/papyrus')} sizes={[768, 1920]} />
            </Column>
          </Row>
        </Container>
        <Spacer
          size={80}
          sizes={{
            [min480]: 100,
            [min1024]: 120,
            [min1440]: 200
          }}
        />
      </Page>
      <Page height="0%" bgColor={colors.highlight}>
        <Container center color="white" flex="1 0 auto">
          <Spacer
            size={60}
            sizes={{
              [min768]: 60,
              [min1024]: 45,
              [min1440]: 80,
              [min1920]: 100
            }}
          />
          <Section center flex alignSelf="stretch">
            <Heading2>Let's work together!</Heading2>
            <Spacer
              size={28}
              sizes={{
                [min768]: 20,
                [min1024]: 28,
                [min1024]: 24
              }}
            />
            <a href="mailto:contact@ninjaconcept.com">
              <SlideButton
                big
                color="white"
                activeColor="white"
                activeFontColor={colors.highlight}
                width={260}
                text="contact@ninjaconcept.com"
                hoverText={
                  <i
                    className="fa fa-envelope-o"
                    aria-hidden="true"
                    style={{
                      fontSize: 25,
                      paddingTop: 10
                    }}
                  />
                }
              />
            </a>
          </Section>
          <Spacer
            size={60}
            sizes={{
              [min768]: 60,
              [min1024]: 45,
              [min1440]: 80,
              [min1920]: 100
            }}
          />
        </Container>
      </Page>
      <Page height="0%" data-scroll-scope="contact" bgColor={colors.background}>
        <Spacer
          size={120}
          sizes={{
            [min768]: 100,
            [min1024]: 120,
            [min1440]: 160
          }}
        />
        <Container color="white" flex="1 0 auto">
          <Row columns={{ [min768]: 12 }} center>
            <Column sizes={{ [min768]: 12 }}>
              <Section alignSelf="stretch" flex="1 0 auto" center>
                <Image svg base={prefixLink('/images/logo/logo_ninjaconcept_horizontal')} />
              </Section>
              <Spacer size={64} />
              <Footer bold textAlign="center">
                © 2016-2024 NinjaConcept GmbH
              </Footer>
              <Footer textAlign="center">
                The design of ninjaconcept is a registered trademark of NinjaConcept GmbH.
                <br />
                Hardtstraße 37A, 76185 Karlsruhe, Germany
              </Footer>
              <Section flex="1 0 auto" alignSelf="stretch" center row columnOnSmall>
                <Footer bold>
                  <a href="tel:+49 721 180 352 30">+49 721 180 352 30</a>
                </Footer>
                <Spacer size={0} sizes={{ [min768]: 18 }} />
                <Footer bold>
                  <a href="mailto:contact@ninjaconcept.com">contact@ninjaconcept.com</a>
                </Footer>
                <Spacer size={0} sizes={{ [min768]: 18 }} />
                <RouterLink to={prefixLink('/impressum/')}>
                  <Footer bold>Impressum</Footer>
                </RouterLink>
                <Spacer size={0} sizes={{ [min768]: 18 }} />
                <RouterLink to={prefixLink('/datenschutz/')}>
                  <Footer bold>Datenschutzerklärung</Footer>
                </RouterLink>
              </Section>
            </Column>
          </Row>
        </Container>
        <Spacer
          size={60}
          sizes={{
            [min1440]: 68,
            [min1920]: 76
          }}
        />
      </Page>
    </Page>
  </FelaProvider>
)

LandingPage.contextTypes = {
  width: PropTypes.number,
  retina: PropTypes.bool
}

export default () => (
  <DeviceProvider>
    <LandingPage />
  </DeviceProvider>
)



// WEBPACK FOOTER //
// ./index.js